// Build-version watcher — detects when the server's bundle differs
// from the bundle currently running in this tab.
//
// Why this exists: our SW lifecycle (skipWaiting + clientsClaim +
// onNeedRefresh → updateSW(true)) is supposed to auto-update tabs on
// deploy, but the timing is fuzzy — an installed PWA can hold the
// previous in-memory bundle for hours even after the SW has rotated.
// Stale bundle + new server can break entire surfaces (e.g. kid chat
// sat on the typing indicator forever after the kid-mode deploy
// because the old client expected token events the new server
// suppresses).
//
// Defer-until-safe strategy. Once a mismatch is detected, we DO NOT
// reload immediately — that would lose any composer text the user is
// mid-typing. Instead:
//   1. Show an "Update available" banner via a window CustomEvent
//      ("hub:update-available"). The <App> tree mounts a small UI
//      that lets the user tap to refresh on their schedule.
//   2. Auto-reload triggers on the next "safe moment":
//        a. visibilitychange → "hidden" — user tabbed away.
//        b. ten minutes of input idle — they've parked the app and
//           moved on with their life.
//   3. The banner has a "Refresh now" button for users who want it
//      immediately.
//
// One watcher covers every surface (login, kid PIN picker, kid app,
// adult shell) because they all live under <App />.

declare const __BUILD_VERSION__: string;

/** Read-only constant — the version this running bundle was built at. */
export const CURRENT_BUILD_VERSION: string =
  typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
// Minimum gap between checks. A burst of focus / visibility events
// (e.g. tabbing between two browser windows) shouldn't hammer the
// server, but we still want a check within ~1s of a real focus.
const MIN_CHECK_GAP_MS = 1000;
// How long the user has to be input-idle before we consider it safe
// to auto-reload. 10 minutes matches what Slack/Discord do — long
// enough that mid-conversation typing isn't interrupted, short enough
// that genuinely-parked tabs catch the update without manual action.
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;

/** Public event the <App> tree listens for to show the update banner. */
export const UPDATE_AVAILABLE_EVENT = "hub:update-available";

let started = false;
let lastCheckAt = 0;
let pendingReload = false;
let reloading = false;
let lastInputAt = Date.now();

/** Tear-the-page-down. Centralised so every trigger funnels here. */
function performReload(reason: string) {
  if (reloading) return;
  reloading = true;
  console.info(`[build-version] reloading (${reason})`);
  // Tiny grace so any in-flight RPC + the console.info make it out
  // before navigation tears the page down.
  setTimeout(() => { window.location.reload(); }, 100);
}

/** Public — called by the "Refresh now" button on the banner. */
export function refreshNow() {
  performReload("user-action");
}

/**
 * Start the watcher. Idempotent. Should be called once from main.tsx
 * after the SW is registered.
 */
export function startBuildVersionWatcher(): void {
  if (started) return;
  started = true;

  // Skip in dev — the SW is disabled there and the bundle version
  // changes on every HMR update which would flap the watcher.
  // Also skip if the bundle didn't get a version stamp (means
  // someone ran the build with no define plugin active).
  if (!CURRENT_BUILD_VERSION) return;
  if (typeof window === "undefined") return;

  const check = async (reason: string) => {
    const now = Date.now();
    if (now - lastCheckAt < MIN_CHECK_GAP_MS) return;
    lastCheckAt = now;
    if (pendingReload || reloading) return;
    try {
      const res = await fetch("/api/build-info", {
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const data = (await res.json()) as { version?: unknown };
      const serverVersion = typeof data?.version === "string" ? data.version : "";
      if (!serverVersion) return;
      if (serverVersion === CURRENT_BUILD_VERSION) return;
      // Mismatch detected. Mark as pending and surface the banner —
      // the actual reload happens via the safe-moment triggers below.
      pendingReload = true;
      console.info(
        `[build-version] new build available (bundle=${CURRENT_BUILD_VERSION} server=${serverVersion} via=${reason})`,
      );
      try {
        window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT, {
          detail: { bundleVersion: CURRENT_BUILD_VERSION, serverVersion },
        }));
      } catch {}
    } catch {
      // Network failures (offline, server unreachable) are silent —
      // the next focus / visibility / interval check will retry.
    }
  };

  // Three poll triggers, in priority order:
  //   1. Tab becomes visible — user returns to it.
  //   2. Window gains focus.
  //   3. Background interval.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void check("visibility-visible");
    } else if (document.visibilityState === "hidden" && pendingReload) {
      // Reload trigger #1: tab went background. Perfect time — the
      // user has stepped away, anything they were doing is on pause,
      // and when they come back they'll land on the new bundle.
      performReload("visibility-hidden");
    }
  });
  window.addEventListener("focus", () => { void check("focus"); });
  window.setInterval(() => { void check("interval"); }, POLL_INTERVAL_MS);
  // First check shortly after boot so a tab opened post-deploy
  // updates without waiting for an explicit focus event.
  window.setTimeout(() => { void check("startup"); }, 3000);

  // Reload trigger #2: input-idle. We watch any keyboard / pointer /
  // touch activity; if it's been quiet for IDLE_TIMEOUT_MS AND a
  // reload is pending, fire it. Checked every 30s; precision doesn't
  // matter (we're already deferred).
  const markActivity = () => { lastInputAt = Date.now(); };
  // {passive: true} keeps these listeners out of the scroll critical
  // path. capture so we catch events even on nested handlers.
  const opts = { passive: true, capture: true } as AddEventListenerOptions;
  window.addEventListener("keydown", markActivity, opts);
  window.addEventListener("pointerdown", markActivity, opts);
  window.addEventListener("touchstart", markActivity, opts);
  window.setInterval(() => {
    if (!pendingReload || reloading) return;
    if (Date.now() - lastInputAt < IDLE_TIMEOUT_MS) return;
    performReload("idle-timeout");
  }, 30_000);
}
